import { COINS_DISABLED } from '../config/coinsFlag';
import React, { useMemo } from "react";

/**
 * BoostTimerBadge (RTL-safe)
 * - Shows ONLY when boost is active and ad is eligible.
 * - Supports EN/AR strings.
 * - Avoids RTL layout bugs by:
 *   - Using dir="auto" + explicit flex direction for icon/text
 *   - Using Intl.NumberFormat for Arabic digits when lang starts with "ar"
 *
 * RULES:
 * - Show ONLY if ad.status='PUBLISHED' AND availability='AVAILABLE' AND is_boosted=true AND boosted_until > now().
 * - Never show for SOLD / DRAFT / HIDDEN.
 * - Text:
 *   EN: "Boosted • {daysLeft}d left" (if <24h then "Boosted • <24h")
 *   AR: "مُعزَّز • {daysLeft} أيام متبقية" (if <24h then "مُعزَّز • أقل من ٢٤ ساعة")
 *
 * Usage:
 * <BoostTimerBadge
 *   isBoosted={ad.is_boosted}
 *   boostedUntil={ad.boosted_until} // ISO string or Date
 *   status={ad.status}              // "published" | "draft" | ...
 *   visibility={ad.visibility}      // "public" | "hidden"
 *   availability={ad.availability_status}  // "available" | "sold"
 *   lang={lang}                     // "en" | "ar"
 * />
 */

function isRtlFromLangOrDoc(lang) {
  if (lang && lang.toLowerCase().startsWith("ar")) return true;
  if (typeof document !== "undefined") {
    const d = document?.documentElement?.getAttribute("dir");
    if (d) return d.toLowerCase() === "rtl";
  }
  return false;
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export default function BoostTimerBadge({
  isBoosted,
  boostedUntil,
  status,
  visibility,
  availability,
  lang = "en",
  className = "",
}) {
  const rtl = isRtlFromLangOrDoc(lang);
  const until = toDate(boostedUntil);

  // Normalize status/visibility/availability to lowercase for comparison
  const normalizedStatus = (status || "").toLowerCase();
  const normalizedVisibility = (visibility || "public").toLowerCase();
  const normalizedAvailability = (availability || "available").toLowerCase();

  const eligible =
    Boolean(isBoosted) &&
    until &&
    normalizedStatus === "published" &&
    normalizedVisibility === "public" &&
    normalizedAvailability === "available" &&
    until.getTime() > Date.now();

  const text = useMemo(() => {
    if (!eligible) return "";
    const msLeft = until.getTime() - Date.now();
    const hoursLeft = msLeft / (1000 * 60 * 60);

    // Under 24 hours
    if (hoursLeft < 24) {
      return lang.toLowerCase().startsWith("ar")
        ? "مُعزَّز • أقل من ٢٤ ساعة"
        : "Boosted • <24h";
    }

    // Days left (ceil)
    const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
    const safeDays = clamp(daysLeft, 1, 365);

    if (lang.toLowerCase().startsWith("ar")) {
      const nf = new Intl.NumberFormat("ar");
      return `مُعزَّز • ${nf.format(safeDays)} أيام متبقية`;
    }

    return `Boosted • ${safeDays}d left`;
  }, [eligible, until, lang]);

  if (COINS_DISABLED) return null;
  if (!eligible) return null;

  // Minimal premium pill styling (no hard dependency)
  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    // subtle premium look
    background: "rgba(255, 153, 0, 0.12)",
    border: "1px solid rgba(255, 153, 0, 0.25)",
    color: "#8A4B00",
    whiteSpace: "nowrap",
  };

  const iconStyle = {
    width: 14,
    height: 14,
    display: "inline-block",
    flex: "0 0 auto",
  };

  // Keep icon-text order consistent in RTL:
  // - We still want icon then text visually, so we use row-reverse when RTL.
  const rowStyle = {
    flexDirection: rtl ? "row-reverse" : "row",
  };

  return (
    <span
      dir="auto"
      style={{ ...baseStyle, ...rowStyle }}
      className={className}
      aria-label={text}
      title={text}
      data-testid="boost-timer-badge"
    >
      <span style={iconStyle} aria-hidden="true">
        🔥
      </span>
      <span style={{ unicodeBidi: "plaintext" }}>{text}</span>
    </span>
  );
}
